import { useCallback, useEffect, useRef, useState, type FocusEvent } from "react";

const scrollQuiet = 5000;

interface SteadyList<T> {
  readonly items: readonly T[];
  readonly waiting: number;
  readonly show: () => void;
  readonly bind: {
    readonly onPointerEnter: () => void;
    readonly onPointerLeave: () => void;
    readonly onFocus: () => void;
    readonly onBlur: (event: FocusEvent<HTMLElement>) => void;
  };
}

export function useSteadyList<T>(items: readonly T[], keyOf: (item: T) => string): SteadyList<T> {
  const pointer = useRef(false);
  const focus = useRef(false);
  const scrolledAt = useRef(0);
  const [frozen, setFrozen] = useState<readonly T[] | null>(null);
  const [last, setLast] = useState(items);
  const inUse = () => pointer.current || focus.current || Date.now() - scrolledAt.current < scrollQuiet;

  if (items !== last) {
    setLast(items);
    if (frozen === null && inUse()) setFrozen(last);
  }

  const release = useCallback(() => {
    if (!(pointer.current || focus.current || Date.now() - scrolledAt.current < scrollQuiet)) setFrozen(null);
  }, []);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const scrolled = () => {
      scrolledAt.current = Date.now();
      clearTimeout(timer);
      timer = setTimeout(release, scrollQuiet);
    };
    addEventListener("scroll", scrolled, { passive: true });
    return () => {
      removeEventListener("scroll", scrolled);
      clearTimeout(timer);
    };
  }, [release]);

  const shown = frozen === null ? items : steady(frozen, items, keyOf);
  const known = frozen === null ? undefined : new Set(frozen.map(keyOf));
  return {
    items: shown,
    waiting: known === undefined ? 0 : items.filter((item) => !known.has(keyOf(item))).length,
    show: () => setFrozen(null),
    bind: {
      onPointerEnter: () => void (pointer.current = true),
      onPointerLeave: () => {
        pointer.current = false;
        release();
      },
      onFocus: () => void (focus.current = true),
      onBlur: (event) => {
        if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
        focus.current = false;
        release();
      },
    },
  };
}

function steady<T>(frozen: readonly T[], items: readonly T[], keyOf: (item: T) => string): readonly T[] {
  const newest = new Map(items.map((item) => [keyOf(item), item]));
  return frozen.map((item) => newest.get(keyOf(item)) ?? item);
}
